import styles from "./ProductSmall.module.css"
import { useRef, useState } from 'react';
import { ProductCardProps } from "../../../types/product.types";
import { getPrimaryProductImage } from "../../../services/productImageService";
import CartButtonSecondary from "../../Button/CartButton/CartButtonSecondary";
import CartAmountSecondary from "../../Button/CartButton/CartAmountSecondary";
import { useCartContext } from "../../../context/CartContext";
import { useDetailContext } from '../../../context/DetailContext';
import { useOrderContext } from "../../../context/OrderContext";
import useTimeModalActions from "../../../hooks/useTimeModalActions";
import { usePreferences } from "../../../context/PreferencesContext";
import { czk_eur_rate } from "../../../constants/prices";
import { czk_to_eur, formatCurrency } from "../../../utils/prices";
import { useTranslation } from "react-i18next";
import { normalizeDate, denormalizeDate } from "../../../utils/calendar";
import Tooltip from "../../Tooltip/Tooltip";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
import { defaultOrderTime } from "../../../constants/times";
import useCartAmountSync from "../../../hooks/useCartAmountSync";
import useCardClickGuard from "../../../hooks/useCardClickGuard";
import { getAdvanceDays, getDeliveryWindow } from "../../../utils/products";
import { getQuantityTooltip } from "../../../utils/quantity";

interface ProductSmallProps extends ProductCardProps {
    onPopup?: boolean // Optional flag - used for handling scroll
}
/**
* Small card for products. Used for recommending products.
*
* @param props ProductCardProps
* @param {Product} props.product - Product,
* @returns TSX.Element
*/
const ProductSmall = ({ product, type = "sales", onPopup }: ProductSmallProps) => {
    const { cartData, reloadCart, cartLoaded } = useCartContext();
    const { t, i18n } = useTranslation(); // Translation
    const { orderData } = useOrderContext(); // Order data
    const { openDetail } = useDetailContext(); // Detail context
    const { openTimeModal } = useTimeModalActions(); // Time modal
    const { selectedCurrency } = usePreferences(); // Current selected currency
    const {isPublicView} = usePublicCatalog();
    
    const dateKey = denormalizeDate(orderData.selectedDate);
    const dateISO = normalizeDate(orderData.selectedDate);
    const { cartAmount, handleCart } = useCartAmountSync({
        cartData,
        cartLoaded,
        reloadCart,
        itemKey: {
            id: product.id,
            category: type,
            dateKey,
            dateISO,
        },
    });

    const buttonRef = useRef<HTMLDivElement>(null)

    // Handle opening detail
    const handleOpen = useCardClickGuard([buttonRef], () => {
        openDetail && openDetail({id: product.id, type: 'sales' }, false, onPopup); 
    });
    // Make sure price is always in correct currency
    const no_dph_price = product.pricing?.price_without_tax != null ? czk_to_eur(product.pricing.price_without_tax, czk_eur_rate, selectedCurrency).toFixed(2) : "N/A"
    const dph_price = product.pricing?.price_with_tax != null ? czk_to_eur(product.pricing.price_with_tax, czk_eur_rate, selectedCurrency).toFixed(2) : "N/A"

    // Order in advance
    const advance = getAdvanceDays(product);

    // Sample time limit
    const { min: minDelivery, max: maxDelivery } = getDeliveryWindow(product);

    const minOrder = product.min_order ?? 1;
    const granularity = product.granularity ?? 1;
    const tooltip = getQuantityTooltip({
        cartAmount,
        minOrder,
        granularity,
        language: i18n.language,
        itemType: "product",
    });
    const [showTooltip, setShowTooltip] = useState(false);
    
    return(
        <>  
            <div className={styles.Product} key={product.id} onClick={handleOpen}>
                <div className={styles.ProductPhoto} style={{ backgroundImage: `url(${getPrimaryProductImage(product)})` }}>
                </div>
                <div className={styles.ProductInfoWrap}>
                    <div className={styles.ProductInfo}>
                        <div className={styles.ProductDesc}>
                            <p>{i18n.language === "en" ? product.name_en || "N/A" : product.name}</p>
                        </div>
                        <div className={styles.ProductPrice}>
                            {product.pricing?.price_without_tax != null && (
                                <p className={styles.NoDPH}>{formatCurrency(no_dph_price, selectedCurrency)} {t("products.no_dph_short")}</p>
                            )}
                            {product.pricing?.price_with_tax != null && (
                                <p className={styles.withDPH}>{formatCurrency(dph_price, selectedCurrency)} {t("products.dph_short")}</p>
                            )}
                        </div>
                    </div>
                    <div className={styles.CartButtons} ref={buttonRef}>
                        {(cartAmount > 0 && !isPublicView) ? (
                            < div className = {styles.Amount} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} >
                        
                            <CartAmountSecondary amountInCart={cartAmount}
                                /*disabledDecrease={cartAmount <= product.min_order}*/
                                disabledIncrease={ cartAmount >= ((product.max_order === null || product.max_order === 0) ? 100 : product.max_order)}
                                onIncrease={() => handleCart(cartAmount +(1*(product.granularity??1)))}
                                onDecrease={()=>{cartAmount > product.min_order ? handleCart(cartAmount -(1*(product.granularity??1))) : handleCart(0)}}
                                colorVariant="primary"
                                    disabled={isPublicView} />
                                {showTooltip && <Tooltip desc={tooltip} className={styles.Tooltips} targetRef={buttonRef}>{tooltip}</Tooltip>}
                            </div>
                        ) : (
                            <CartButtonSecondary
                                text={t("buttons.order")}
                                onClick={() =>
                                    openTimeModal(
                                        {
                                            type: "single",
                                            date: orderData.selectedDate,
                                            time: defaultOrderTime,
                                            productId: product.id,
                                            prodType: type,
                                            static: true,
                                            amount: Math.max(product.min_order, product.granularity) ?? 1,
                                            restrictions: { min: minDelivery, max: maxDelivery },
                                            advance: advance,
                                        },
                                        { openOnTop: onPopup }
                                    )
                                }
                                className={styles.CartButton}
                                colorVariant="primary"
                                disabled={isPublicView}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    )
};

export default ProductSmall;
